import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { loginSchema, type Login } from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { rotaInicialDoUsuario, LAST_MODULE_KEY } from "@/lib/modules";
import { destinoDeVolta } from "@/lib/volta-login";
function RizomaLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Rizoma"
    >
      <rect width="44" height="44" rx="9" fill="#2D5016" />
      <circle cx="14" cy="22" r="4.5" fill="white" />
      <circle cx="27" cy="14" r="3.5" fill="white" opacity="0.75" />
      <circle cx="27" cy="30" r="3.5" fill="white" opacity="0.75" />
      <circle cx="37" cy="22" r="2.5" fill="#C17BC0" opacity="0.9" />
      <line x1="14" y1="22" x2="24" y2="15" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="14" y1="22" x2="24" y2="29" stroke="white" strokeWidth="1.4" opacity="0.5" />
      <line x1="27" y1="14" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
      <line x1="27" y1="30" x2="35" y2="22" stroke="#C17BC0" strokeWidth="1.2" opacity="0.5" />
    </svg>
  );
}

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { login: authLogin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<Login>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identificador: "",
      password: "",
      // Desmarcado por padrão: aparelho compartilhado não fica logado por 30 dias.
      manterConectado: false,
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: Login) => {
      const response = await apiRequest("POST", "/api/auth/login", data);
      return await response.json();
    },
    onSuccess: (data) => {
      // Token não mais armazenado em localStorage — gerenciado via cookie httpOnly
      // Armazenamos apenas o user object (não-sensível) para restauração rápida no boot
      localStorage.setItem("user", JSON.stringify(data.user));
      authLogin(data.user);
      toast({
        title: "Login realizado com sucesso!",
        description: `Bem-vindo, ${data.user.name}`,
      });
      setTimeout(() => {
        // ⚠️ NÃO "/cacho/inicio". O corretor que acaba de criar acesso pelo romaneio
        // tem uma org com 'mercado' e nada mais — mandá-lo para o Cacho é abrir uma
        // tela que a própria API recusa, e ainda carimba "cacho" como último módulo.
        // Quem decide é `modules_enabled`, que acabou de chegar na resposta do login.
        let ultimo: string | null = null;
        try {
          ultimo = localStorage.getItem(LAST_MODULE_KEY);
        } catch {
          // localStorage indisponível (aba privada) — sem "onde parei", segue no primeiro.
        }
        // Veio de um link (o aviso do WhatsApp, com a sessão vencida)? Volta para
        // ele. Só caminho interno — ver `destinoDeVolta`.
        const volta = destinoDeVolta(new URLSearchParams(window.location.search).get("volta"));
        if (volta) {
          setLocation(volta, { replace: true } as any);
          return;
        }
        // Sem módulo nenhum, a raiz é quem mostra o que fazer — não existe home para ir.
        // `modulos` é o efetivo do USUÁRIO (org ∩ restrição dele) — PR 1.
        setLocation(rotaInicialDoUsuario(data.user?.modulos ?? data.user?.modulesEnabled ?? [], ultimo) ?? "/");
      }, 0);
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao fazer login",
        description: error.message || "Credenciais inválidas",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: Login) => {
    setIsLoading(true);
    loginMutation.mutate(data, {
      onSettled: () => setIsLoading(false),
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center gap-4">
            <RizomaLogo className="h-16 w-16" />
            <div>
              <CardTitle className="text-3xl font-bold">Rizoma Farm</CardTitle>
              <CardDescription className="text-base mt-1">
                Gestão da produção de banana: ciclos, mangarás e colheitas.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              {/* Uma caixa só, e-mail OU telefone (Mercado C2 — o corretor entra pelo
                  número, porque muitos não têm e-mail de uso corrente). `type="text"`,
                  não `email`: com `type="email"` o próprio navegador recusaria um
                  telefone antes de a tela ver. O `data-testid` continua `input-email`
                  para não quebrar teste existente que aponta para ele. */}
              <div className="space-y-2">
                <Label htmlFor="identificador" className="text-sm font-medium">
                  E-mail ou telefone
                </Label>
                <Input
                  id="identificador"
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  placeholder="seu@email.com ou (74) 99999-8888"
                  data-testid="input-email"
                  {...form.register("identificador")}
                  className="w-full"
                />
                {form.formState.errors.identificador && (
                  <p className="text-xs text-destructive mt-1">
                    {form.formState.errors.identificador.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Senha
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••"
                  data-testid="input-password"
                  {...form.register("password")}
                  className="w-full"
                />
                {form.formState.errors.password && (
                  <p className="text-xs text-destructive mt-1">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              <label htmlFor="manter-conectado" className="flex min-h-11 cursor-pointer items-center gap-3">
                <Checkbox
                  id="manter-conectado"
                  checked={form.watch("manterConectado") === true}
                  onCheckedChange={v => form.setValue("manterConectado", v === true)}
                  data-testid="checkbox-manter-conectado"
                />
                <span className="text-sm">Manter conectado neste aparelho</span>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              data-testid="button-login"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Entrar
            </Button>
            <p className="text-center text-sm">
              <Link href="/esqueci-senha" className="text-muted-foreground underline underline-offset-2" data-testid="link-esqueci-senha">
                Esqueci minha senha
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
